import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";

const field = "w-full rounded-xl border-2 border-[var(--ink)] px-3 py-2 text-sm font-semibold outline-none focus:bg-[var(--paper)]";
const emptyBranch = () => ({ label: "", link: "", children: [""] });

const BranchEditor = ({ b, i, onChange, onRemove }) => {
  const set = (k, v) => onChange({ ...b, [k]: v });
  const setChild = (j, v) => set("children", b.children.map((c, x) => (x === j ? v : c)));
  return (
    <div className="ink-box-sm space-y-2 rounded-2xl bg-[var(--paper)] p-3" data-testid={`map-editor-branch-${i}`}>
      <div className="flex gap-2">
        <input value={b.link} maxLength={40} onChange={(e) => set("link", e.target.value)} placeholder="Collegamento (es. produce)" data-testid={`map-editor-link-${i}`} className={field + " sm:w-48"} />
        <input value={b.label} maxLength={60} onChange={(e) => set("label", e.target.value)} placeholder="Concetto" data-testid={`map-editor-label-${i}`} className={field + " font-extrabold"} />
        <button onClick={onRemove} data-testid={`map-editor-remove-branch-${i}`} aria-label="Elimina ramo" className="rounded-xl border-2 border-[var(--ink)] bg-white px-3 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
      </div>
      {b.children.map((c, j) => (
        <div key={j} className="flex gap-2 pl-4">
          <input value={c} maxLength={80} onChange={(e) => setChild(j, e.target.value)} placeholder="Idea collegata" data-testid={`map-editor-child-${i}-${j}`} className={field} />
          <button onClick={() => set("children", b.children.filter((_, x) => x !== j))} data-testid={`map-editor-remove-child-${i}-${j}`} aria-label="Elimina idea" className="rounded-xl border-2 border-[var(--ink)] bg-white px-3 hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {b.children.length < 3 && (
        <button onClick={() => set("children", [...b.children, ""])} data-testid={`map-editor-add-child-${i}`} className="ml-4 flex items-center gap-1 text-xs font-extrabold hover:underline"><Plus className="h-3.5 w-3.5" /> Aggiungi idea</button>
      )}
    </div>
  );
};

export const MapEditor = ({ term, onClose, onSaved }) => {
  const [center, setCenter] = useState("");
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!term) return;
    const m = term.concept_map;
    setCenter(m?.center || term.word);
    setBranches(m?.branches?.length ? m.branches.map((b) => ({ ...b, children: [...b.children] })) : [emptyBranch(), emptyBranch()]);
  }, [term]);

  const save = async () => {
    const clean = branches.filter((b) => b.label.trim());
    if (!center.trim() || clean.length < 2) return toast.error("Servono la parola centrale e almeno 2 concetti");
    setSaving(true);
    try {
      await api.put(`/terms/${term.id}/map`, { center, branches: clean.map((b) => ({ ...b, children: b.children.filter((c) => c.trim()) })) });
      toast.success("Mappa salvata");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!term} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl border-[2.5px] border-[var(--ink)] bg-white sm:max-w-2xl" data-testid="map-editor-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Modifica mappa: {term?.word}</DialogTitle></DialogHeader>
        <label className="text-sm font-extrabold">Parola centrale</label>
        <input value={center} maxLength={60} onChange={(e) => setCenter(e.target.value)} data-testid="map-editor-center-input" className={field} />
        <div className="space-y-3">
          {branches.map((b, i) => (
            <BranchEditor key={i} b={b} i={i} onChange={(nb) => setBranches(branches.map((x, k) => (k === i ? nb : x)))} onRemove={() => setBranches(branches.filter((_, k) => k !== i))} />
          ))}
        </div>
        {branches.length < 6 && (
          <button onClick={() => setBranches([...branches, emptyBranch()])} data-testid="map-editor-add-branch" className="flex items-center gap-1 text-sm font-extrabold hover:underline"><Plus className="h-4 w-4" /> Aggiungi concetto</button>
        )}
        <button onClick={save} disabled={saving} data-testid="map-editor-save-button" className="ink-box-sm ink-press flex items-center justify-center gap-2 rounded-full bg-[var(--sky)] py-3 font-extrabold text-white disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva mappa
        </button>
      </DialogContent>
    </Dialog>
  );
};
